'use client'

import { useState } from 'react'

import Cards from '@/ui/assets/components/cards.inline.svg'
import { Button } from '@/ui/atoms/button/Button'
import { Checkbox } from '@/ui/atoms/checkbox/Checkbox'
import { Panel } from '@/ui/atoms/panel/Panel'
import { Typography } from '@/ui/atoms/typography/Typography'
import { cn } from '@/ui/utils/style'
import { ArrowRight, CheckCircle2, FileText, PartyPopper } from 'lucide-react'
import { useCardAutoSelection } from '../hooks/useCardAutoSelection'

const THIRD_NATIONAL_PRIVACY_POLICY_URL = 'https://www.3rdnational.com/privacypolicy'

interface CardUserAgreementsProps {
  onAcceptAgreements: () => void
  isUSUser?: boolean
}

export function CardUserAgreements({ onAcceptAgreements, isUSUser = false }: CardUserAgreementsProps) {
  // Warm the first-card auto-selection caches (solver options, Euler positions,
  // borrow pairs, spending-power probe) while the user reads and ticks agreement
  // checkboxes, so the auto-create flow can fire immediately on accept instead of
  // waiting on the indexer and multicall round-trips.
  useCardAutoSelection(true)

  const [showKYCApproval, setShowKYCApproval] = useState(true)
  const [acceptedESignConsent, setAcceptedESignConsent] = useState(false)
  const [acceptedCardTerms, setAcceptedCardTerms] = useState(false)
  const [acceptedInformationAccuracy, setAcceptedInformationAccuracy] = useState(false)
  const [acceptedNoUnauthorizedSolicitation, setAcceptedNoUnauthorizedSolicitation] = useState(false)
  const [acceptedUSPrivacyNotice, setAcceptedUSPrivacyNotice] = useState(false)
  const [acceptedIssuerDisclaimer, setAcceptedIssuerDisclaimer] = useState(false)

  const canProceed = isUSUser
    ? acceptedESignConsent &&
      acceptedCardTerms &&
      acceptedInformationAccuracy &&
      acceptedNoUnauthorizedSolicitation &&
      acceptedUSPrivacyNotice &&
      acceptedIssuerDisclaimer
    : acceptedESignConsent &&
      acceptedCardTerms &&
      acceptedInformationAccuracy &&
      acceptedNoUnauthorizedSolicitation &&
      acceptedIssuerDisclaimer

  const handleContinueFromKYC = () => {
    setShowKYCApproval(false)
  }

  const handleAccept = () => {
    if (canProceed) {
      onAcceptAgreements()
    }
  }

  // Show KYC Approval message first
  if (showKYCApproval) {
    return (
      <div className="flex flex-col items-center gap-12 lg:flex-row lg:items-start">
        <div className="flex flex-1 flex-col gap-6">
          <Panel.Wrapper className="relative w-full max-w-xl overflow-hidden">
            <div className="pointer-events-none absolute inset-0 rounded-lg bg-gradient-to-br from-gradient-yellow/20 via-transparent to-gradient-green/20" />

            <Panel.Content className="relative flex flex-col gap-6 p-8">
              <div className="flex items-center gap-3">
                <div className="flex h-12 w-12 items-center justify-center rounded-full bg-gradient-to-br from-gradient-yellow to-gradient-green">
                  <PartyPopper className="h-6 w-6 text-black" />
                </div>
                <div>
                  <Typography variant="h3" gradient>
                    KYC Approved
                  </Typography>
                </div>
              </div>

              <Typography variant="p" className="text-muted-foreground">
                Congratulations! Your identity verification has been successfully completed. You can now proceed to
                review and accept the user agreements.
              </Typography>

              <div className="flex flex-col gap-3 rounded-lg border border-border/50 bg-background/30 p-4">
                <div className="flex items-center gap-2">
                  <CheckCircle2 className="h-4 w-4 text-primary" />
                  <Typography variant="p" className="text-foreground text-sm">
                    Identity verified
                  </Typography>
                </div>
                <div className="flex items-center gap-2">
                  <div className="h-4 w-4 rounded-full border-2 border-muted-foreground/50 border-dashed" />
                  <Typography variant="p" className="text-muted-foreground text-sm">
                    Accept user agreements
                  </Typography>
                </div>
                <div className="flex items-center gap-2">
                  <div className="h-4 w-4 rounded-full border-2 border-muted-foreground/50 border-dashed" />
                  <Typography variant="p" className="text-muted-foreground text-sm">
                    Get your virtual card
                  </Typography>
                </div>
              </div>

              <Button
                onClick={handleContinueFromKYC}
                size="lg"
                className={cn(
                  'w-full bg-gradient-to-r from-gradient-yellow to-gradient-green text-black',
                  'transition-all duration-300 hover:opacity-90 hover:shadow-lg hover:shadow-primary/20',
                )}
                postfixIcon={<ArrowRight className="h-5 w-5" />}
              >
                Continue
              </Button>
            </Panel.Content>
          </Panel.Wrapper>
        </div>

        <div className="relative flex items-center justify-center lg:flex-1">
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="h-[250px] w-[350px] rounded-full bg-gradient-to-br from-gradient-yellow/20 to-gradient-green/20 blur-[60px]" />
          </div>
          <div className="relative drop-shadow-[0_0_40px_rgba(251,229,114,0.2)] transition-transform duration-500 hover:scale-105">
            <Cards className="h-auto w-full max-w-sm" />
          </div>
        </div>
      </div>
    )
  }

  // Show User Agreements after KYC approval acknowledgment
  return (
    <div className="flex flex-col items-center gap-12 lg:flex-row lg:items-start">
      <div className="flex flex-1 flex-col gap-6">
        <Panel.Wrapper className="relative w-full max-w-xl overflow-hidden">
          <div className="pointer-events-none absolute inset-0 rounded-lg bg-gradient-to-br from-primary/10 via-transparent to-gradient-green/10" />

          <Panel.Content className="relative flex flex-col gap-6 p-8">
            <div className="flex items-center gap-3">
              <div className="flex h-12 w-12 items-center justify-center rounded-full border-2 border-primary/30 bg-primary/10">
                <FileText className="h-6 w-6 text-primary" />
              </div>
              <div>
                <Typography variant="h3" gradient>
                  User Agreements
                </Typography>
              </div>
            </div>

            <Typography variant="p" className="text-muted-foreground">
              Please review and accept the following agreements to continue
            </Typography>

            <div className="flex flex-col gap-5 rounded-lg border border-border/50 bg-background/30 p-5">
              {/* E-Sign Consent */}
              <div className="flex items-start gap-3">
                <Checkbox
                  id="esign-consent-checkbox"
                  checked={acceptedESignConsent}
                  onCheckedChange={(checked) => setAcceptedESignConsent(checked === true)}
                  className="mt-0.5"
                />
                <label htmlFor="esign-consent-checkbox" className="cursor-pointer text-sm leading-relaxed">
                  I accept the{' '}
                  <a
                    href="/card/esign-consent"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-primary underline hover:text-primary/80"
                  >
                    E-Sign Consent
                  </a>
                </label>
              </div>

              {/* Account Opening Privacy Notice - US Users Only */}
              {isUSUser && (
                <div className="flex items-start gap-3">
                  <Checkbox
                    id="us-privacy-notice-checkbox"
                    checked={acceptedUSPrivacyNotice}
                    onCheckedChange={(checked) => setAcceptedUSPrivacyNotice(checked === true)}
                    className="mt-0.5"
                  />
                  <label htmlFor="us-privacy-notice-checkbox" className="cursor-pointer text-sm leading-relaxed">
                    I accept the{' '}
                    <a
                      href="/card/account-opening-privacy-notice"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-primary underline hover:text-primary/80"
                    >
                      Account Opening Privacy Notice
                    </a>
                  </label>
                </div>
              )}

              {/* Card Terms and Issuer Privacy Policy */}
              <div className="flex items-start gap-3">
                <Checkbox
                  id="card-terms-checkbox"
                  checked={acceptedCardTerms}
                  onCheckedChange={(checked) => setAcceptedCardTerms(checked === true)}
                  className="mt-0.5"
                />
                <label htmlFor="card-terms-checkbox" className="cursor-pointer text-sm leading-relaxed">
                  I accept the{' '}
                  <a
                    href={isUSUser ? '/card/us-cardholder-agreement' : '/card/card-terms'}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-primary underline hover:text-primary/80"
                  >
                    HypurrFi Card Terms
                  </a>
                  , and the{' '}
                  <a
                    href={THIRD_NATIONAL_PRIVACY_POLICY_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-primary underline hover:text-primary/80"
                  >
                    Issuer Privacy Policy
                  </a>
                </label>
              </div>

              {/* Information Accuracy Certification */}
              <div className="flex items-start gap-3">
                <Checkbox
                  id="information-accuracy-checkbox"
                  checked={acceptedInformationAccuracy}
                  onCheckedChange={(checked) => setAcceptedInformationAccuracy(checked === true)}
                  className="mt-0.5"
                />
                <label htmlFor="information-accuracy-checkbox" className="cursor-pointer text-sm leading-relaxed">
                  I certify that the information I have provided is accurate and that I will abide by all the rules and
                  requirements related to my HypurrFi Spend Card.
                </label>
              </div>

              {/* No Unauthorized Solicitation Acknowledgment */}
              <div className="flex items-start gap-3">
                <Checkbox
                  id="no-solicitation-checkbox"
                  checked={acceptedNoUnauthorizedSolicitation}
                  onCheckedChange={(checked) => setAcceptedNoUnauthorizedSolicitation(checked === true)}
                  className="mt-0.5"
                />
                <label htmlFor="no-solicitation-checkbox" className="cursor-pointer text-sm leading-relaxed">
                  I acknowledge that applying for the HypurrFi Spend Card does not constitute unauthorized solicitation.
                </label>
              </div>

              {/* Issuer and DeFi Protocol Disclaimer */}
              <div className="flex items-start gap-3">
                <Checkbox
                  id="issuer-disclaimer-checkbox"
                  checked={acceptedIssuerDisclaimer}
                  onCheckedChange={(checked) => setAcceptedIssuerDisclaimer(checked === true)}
                  className="mt-0.5"
                />
                <label htmlFor="issuer-disclaimer-checkbox" className="cursor-pointer text-sm leading-relaxed">
                  The HypurrFi Spend Card is provided by Issuer pursuant to a license from Visa. Any credit issued by
                  the HypurrFi Protocol and other DeFi protocols (collectively, &quot;The DeFi Protocols&quot;) is
                  subject to its separate terms and conditions. Issuer is not a party to any agreement with The DeFi
                  Protocols and is not responsible for any loan or credit arrangement between user and The DeFi
                  Protocols.
                </label>
              </div>
            </div>

            <Button
              onClick={handleAccept}
              disabled={!canProceed}
              size="lg"
              className={cn(
                'w-full transition-all duration-300',
                canProceed
                  ? 'bg-gradient-to-r from-gradient-yellow to-gradient-green text-black hover:opacity-90 hover:shadow-lg hover:shadow-primary/20'
                  : '',
              )}
              postfixIcon={<ArrowRight className="h-5 w-5" />}
            >
              Get My Card
            </Button>
          </Panel.Content>
        </Panel.Wrapper>
      </div>

      <div className="relative flex items-center justify-center lg:flex-1">
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="h-[250px] w-[350px] rounded-full bg-gradient-to-br from-primary/15 to-gradient-green/15 blur-[60px]" />
        </div>
        <div className="relative drop-shadow-[0_0_40px_rgba(251,229,114,0.15)] transition-transform duration-500 hover:scale-105">
          <Cards className="h-auto w-full max-w-sm" />
        </div>
      </div>
    </div>
  )
}
